import { FC, useState } from "react"
import { CheckCircle2, Pencil, Trash2 } from "lucide-react"
import { LinkBtn } from "../buttons"
import CopyTextButton from "../copy-text-button"
import If from "../if"
import InputURL from "./input-url"
import { ApprovedUrl } from "../../../types"
import {
  useDeleteApproverURLMutation,
  useUpdateApprovedURLMutation
} from "../../../redux/queries"

type Props = {
  url: ApprovedUrl
  /** Leading green "already approved" status icon. Off by default. */
  showStatusIcon?: boolean
  /** Trailing copy-to-clipboard icon, alongside edit/delete. Off by default. */
  showCopyButton?: boolean
  /** Row padding — override for contexts denser/looser than the modal's default list. */
  className?: string
}

const ListItem: FC<Props> = ({
  url,
  showStatusIcon = false,
  showCopyButton = false,
  className = "py-1 px-0.5"
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editableURL, setEditableURL] = useState(url.domain_pattern)

  const [updateApprovedURL] = useUpdateApprovedURLMutation()
  const [deleteApproverURL] = useDeleteApproverURLMutation()

  const handleDelete = () => {
    deleteApproverURL(url.id)
  }

  const onSubmit = () => {
    updateApprovedURL({
      id: url.id,
      domain_pattern: editableURL,
      org_id: url.org_id
    })
    setIsEditing(false)
  }

  return (
    <li
      className={`flex items-center justify-between border-b border-zinc-200 ${className}`}
    >
      <If
        condition={isEditing}
        then={
          <InputURL
            url={editableURL}
            onChange={setEditableURL}
            onSubmit={onSubmit}
            onCancel={() => {
              setEditableURL(url.domain_pattern)
              setIsEditing(false)
            }}
          />
        }
        else={
          <>
            <div className="flex min-w-0 items-center gap-2">
              {showStatusIcon && (
                <CheckCircle2
                  size={16}
                  className="shrink-0 text-green-500"
                  aria-hidden="true"
                />
              )}
              <span className="text-base-dark truncate pr-3">
                {url.domain_pattern}
              </span>
            </div>
            <div className="flex shrink-0 items-center">
              {showCopyButton && (
                <CopyTextButton
                  text={url.domain_pattern}
                  showCopiedValue
                  className="text-sm py-2 px-2"
                />
              )}
              <LinkBtn
                theme="alt"
                className="text-sm py-2 px-2"
                onClick={() => {
                  setIsEditing(true)
                }}
              >
                <Pencil size={14} />
              </LinkBtn>
              <LinkBtn
                theme="alt"
                className="text-sm py-2 px-2"
                onClick={handleDelete}
              >
                <Trash2 size={14} />
              </LinkBtn>
            </div>
          </>
        }
      />
    </li>
  )
}

export default ListItem
